/**
 * SqliteClient — opens the database and runs statements against it.
 *
 * Its single job is executing SQL. It does not know what a project or a photo
 * is, and it does not decide when to persist — that belongs to DbPersister.
 */

import initSqlJs from 'sql.js';
import type { Database, SqlJsStatic } from 'sql.js';
import { SCHEMA_SQL } from '../infra/schema';
import type { DbStorage } from './db-storage';

export type SqlValue = number | string | Uint8Array | null;
export type SqlRow = Record<string, SqlValue>;

export interface SqliteOptions {
  /** Vite hands us a hashed URL for the wasm; Node resolves it from node_modules. */
  locateFile?: (file: string) => string;
}

let runtime: Promise<SqlJsStatic> | null = null;

function sqlJs(options: SqliteOptions): Promise<SqlJsStatic> {
  runtime ??= initSqlJs(options.locateFile ? { locateFile: options.locateFile } : undefined);
  return runtime;
}

export class SqliteClient {
  /** Nesting level, so an inner transaction() uses a savepoint rather than BEGIN. */
  private depth = 0;

  private constructor(private readonly db: Database) {}

  static async open(storage: DbStorage, options: SqliteOptions = {}): Promise<SqliteClient> {
    const SQL = await sqlJs(options);
    const existing = await storage.load();

    const db = existing ? new SQL.Database(existing) : new SQL.Database();
    db.run('PRAGMA foreign_keys = ON;');
    db.run(SCHEMA_SQL);

    return new SqliteClient(db);
  }

  /** Every row matching the query, as plain objects. */
  all<T = SqlRow>(sql: string, params: SqlValue[] = []): T[] {
    const stmt = this.db.prepare(sql);
    try {
      stmt.bind(params);
      const rows: T[] = [];
      while (stmt.step()) rows.push(stmt.getAsObject() as T);
      return rows;
    } finally {
      stmt.free();
    }
  }

  /** The first matching row, or null. */
  get<T = SqlRow>(sql: string, params: SqlValue[] = []): T | null {
    const stmt = this.db.prepare(sql);
    try {
      stmt.bind(params);
      return stmt.step() ? (stmt.getAsObject() as T) : null;
    } finally {
      stmt.free();
    }
  }

  /** A single scalar, or null when the query returned no row. */
  scalar<T extends SqlValue = SqlValue>(sql: string, params: SqlValue[] = []): T | null {
    const row = this.get(sql, params);
    if (!row) return null;
    const first = Object.values(row)[0];
    return (first ?? null) as T | null;
  }

  run(sql: string, params: SqlValue[] = []): { lastId: number; changes: number } {
    this.db.run(sql, params);
    return {
      lastId: Number(this.db.exec('SELECT last_insert_rowid()')[0]?.values[0]?.[0] ?? 0),
      changes: this.db.getRowsModified(),
    };
  }

  /** Multiple statements, no parameters. Used for schema and migrations. */
  exec(sql: string): void {
    this.db.run(sql);
  }

  /**
   * Runs `fn` atomically; any throw rolls the whole thing back.
   *
   * Re-entrant: SQLite has no nested BEGIN, so an inner call opens a savepoint
   * instead. That lets a service wrap repository methods that transact
   * internally without the two fighting over the same transaction.
   */
  transaction<T>(fn: () => T): T {
    const savepoint = `m3_sp_${this.depth}`;
    this.db.run(this.depth === 0 ? 'BEGIN' : `SAVEPOINT ${savepoint}`);
    this.depth++;

    try {
      const result = fn();
      this.depth--;
      this.db.run(this.depth === 0 ? 'COMMIT' : `RELEASE ${savepoint}`);
      return result;
    } catch (err) {
      this.depth--;
      if (this.depth === 0) {
        this.db.run('ROLLBACK');
      } else {
        this.db.run(`ROLLBACK TO ${savepoint}`);
        this.db.run(`RELEASE ${savepoint}`);
      }
      throw err;
    }
  }

  /** The whole database as bytes, ready to hand to storage. */
  snapshot(): Uint8Array {
    return this.db.export();
  }

  close(): void {
    this.db.close();
  }
}
